import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import { useTranslation } from 'react-i18next';

import 'shared/styles/hide-hubspot.css';

import {
  GenerateReportsWrapper,
  GenerateReportsPage,
  GenerateReportsFooter,
  GenerateReportsHeaderBig,
  GenerateReportsHeaderSection,
} from 'ui/GenerateReports';

import {
  distributionService,
  impactScoreService,
  peergroupComparisonService,
  regionImpactService,
  industryImpactService,
  scoreOverviewService,
  esgAvailable as impactAvailable,
  externalCostsComparisonService,
  impactOverviewService,
  rankingService,
  fetchESGScoreSummaryTab,
} from 'services/esg';
import { fetchEntityBasic } from 'services';
import format from 'date-fns/format';
import { ratingTypes } from 'shared/constants/ratingTypes';
import { disclaimerDocument } from 'shared/constants/disclaimer';
import { useFeatures } from 'shared/hooks';
import {
  regionImpactToMapParser,
  regionImpactToTableParser,
  industryImpactToTableParser,
  scoreOverviewParser,
} from 'shared/parsers/esg';

import { longDateFormat } from 'shared/constants/dateFormat';
import { peergroupComparisonParser } from 'shared/parsers';
import {
  ESGDistribution,
  ESGImpactScore,
  ESGImpactMap,
  ESGImpactTable,
  ESGScoreComparison,
  ESGImpactDistribution,
  ESGScoreOverview,
  ESGImpactOverview,
  ESGRanking,
  ESGLendingData,
  ESGLendingImpact,
} from 'modules/ESG';
import {
  getPieByRegion,
  getPieByIndustry,
  getImpactByCountry,
  getImpactByIndustry,
} from '../../modules/ESG/services/esgLendingService';

function useQueryParams() {
  const params = new URLSearchParams(window.location.search);
  return { year: params.get('year'), latestUpdate: params.get('latestUpdate') };
}

export const GenerateReportESG = () => {
  const { entityId } = useParams();
  const { year, latestUpdate } = useQueryParams();

  const [pages, setPages] = useState(6);
  const { esgLendingData } = useFeatures();

  const [impactScore, setImpactScore] = useState({ isPending: true, data: null });
  const [esgAvailable, setEsgAvailable] = useState(false);
  const [scoreComparison, setScoreComparison] = useState({ isPending: true, data: [] });
  const [externalCostsComparison, setExternalCostsComparison] = useState({
    isPending: true,
    data: null,
  });
  const [scoreOverview, setScoreOverview] = useState({ isPending: true, data: [] });
  const [regionImpact, setRegionImpact] = useState({ isPending: true, data: [] });
  const [industryImpact, setIndustryImpact] = useState({ isPending: true, data: [] });
  const [distribution, setDistribution] = useState({ isPending: true, data: null });
  const [impactOverview, setImpactOverview] = useState({ isPending: true, data: null });
  const [ranking, setRanking] = useState({ isPending: true, data: null });
  const [entityData, setEntityData] = useState({
    isPending: true,
    data: null,
  });

  const [stats, setStats] = useState({ isPending: true, regions: [], industries: [] });
  const [industryData, setIndustryData] = useState({ isPending: true, data: [] });
  const [countryData, setCountryData] = useState({ isPending: true, data: [] });
  const [showBankLendingSection, setShowBankLendingSection] = useState(false);

  const { t } = useTranslation();
  const didUnmount = useRef(false);
  const actualDate = format(new Date(), longDateFormat);

  const getEsgAvailable = async () => {
    try {
      const data = await impactAvailable(entityId);
      if (!didUnmount.current) {
        const available = data ? data.data_exists : false;
        setEsgAvailable(available);
        return available;
      }
    } catch (error) {
      if (!didUnmount.current) {
        setEsgAvailable(false);
      }
    }
    return false;
  };

  const getEntityData = async () => {
    setEntityData((data) => ({ ...data, isPending: true }));

    try {
      const response = await fetchEntityBasic({
        id: entityId,
        ratingType: ratingTypes.ESG_IMPACT,
        params: { year, latestUpdate },
      });

      if (!didUnmount.current) {
        setEntityData(() => ({
          isPending: false,
          data: response.success ? response.data : null,
        }));
      }
    } catch (error) {
      if (!didUnmount.current) {
        setEntityData(() => ({ isPending: false, data: null }));
      }
    }
  };

  const getEntityDataESGScore = async () => {
    const response = await fetchESGScoreSummaryTab(entityId, { year, latestUpdate });
    checkIfBank(response.companyType);
  };

  const getImpactScore = async () => {
    setImpactScore((data) => ({ ...data, isPending: true }));
    try {
      const response = await impactScoreService(entityId, { year, latestUpdate });
      if (!didUnmount.current) {
        setImpactScore(() => ({
          isPending: false,
          data: response.success ? response.total_score : null,
        }));
      }
    } catch (error) {
      if (!didUnmount.current) {
        setImpactScore(() => ({ isPending: false, data: null }));
      }
    }
  };

  const getScoreOverview = async () => {
    setScoreOverview((data) => ({ ...data, isPending: true }));
    try {
      const data = await scoreOverviewService(entityId, { year, latestUpdate });
      if (!didUnmount.current) {
        setScoreOverview(() => ({
          isPending: false,
          data: data.error ? [] : scoreOverviewParser(data),
        }));
      }
    } catch (error) {
      if (!didUnmount.current) {
        setScoreOverview(() => ({ isPending: false, data: [] }));
      }
    }
  };

  const getScoreComparison = async () => {
    setScoreComparison((data) => ({ ...data, isPending: true }));
    try {
      const data = await peergroupComparisonService(entityId, { year, latestUpdate });
      if (!didUnmount.current) {
        setScoreComparison(() => ({
          isPending: false,
          data: data.error ? [] : peergroupComparisonParser(data),
        }));
      }
    } catch (error) {
      if (!didUnmount.current) {
        setScoreComparison(() => ({ isPending: false, data: [] }));
      }
    }
  };

  const getRegionImpact = async () => {
    setRegionImpact({
      ...regionImpact,
      isPending: true,
    });

    try {
      const data = await regionImpactService(entityId, { year, latestUpdate });
      if (!didUnmount.current) {
        setRegionImpact({ isPending: false, data });
      }
    } catch (error) {
      if (!didUnmount.current) {
        setRegionImpact({ isPending: false, data: [] });
      }
    }
  };

  const getIndustryImpact = async () => {
    setIndustryImpact((data) => ({ ...data, isPending: true }));
    try {
      const data = await industryImpactService(entityId, { year, latestUpdate });
      if (!didUnmount.current) {
        setIndustryImpact(() => ({
          isPending: false,
          data: data.error ? [] : industryImpactToTableParser(data),
        }));
      }
    } catch (error) {
      if (!didUnmount.current) {
        setIndustryImpact(() => ({ isPending: false, data: [] }));
      }
    }
  };

  const getDistribution = async () => {
    setDistribution((data) => ({ ...data, isPending: true }));
    try {
      const data = await distributionService(entityId, { year, latestUpdate });
      if (!didUnmount.current) {
        setDistribution(() => ({
          isPending: false,
          data: data.error ? null : data,
        }));
      }
    } catch (error) {
      if (!didUnmount.current) {
        setDistribution(() => ({ isPending: false, data: null }));
      }
    }
  };

  const getImpactOverview = async () => {
    setImpactOverview((data) => ({ ...data, isPending: true }));
    try {
      const data = await impactOverviewService(entityId, { year, latestUpdate });

      if (!didUnmount.current) {
        setImpactOverview(() => ({
          isPending: false,
          data: data || null,
        }));
      }
    } catch (error) {
      if (!didUnmount.current) {
        setImpactOverview(() => ({ isPending: false, data: null }));
      }
    }
  };

  const getRanking = async () => {
    setRanking((data) => ({ ...data, isPending: true }));
    try {
      const data = await rankingService(entityId, { year, latestUpdate });
      if (!didUnmount.current) {
        setRanking(() => ({
          isPending: false,
          data: data || null,
        }));
      }
    } catch (error) {
      if (!didUnmount.current) {
        setRanking(() => ({ isPending: false, data: null }));
      }
    }
  };

  const getExternalCostsComparison = async () => {
    setExternalCostsComparison((data) => ({ ...data, isPending: true }));
    try {
      const data = await externalCostsComparisonService(entityId, { year, latestUpdate });
      if (!didUnmount.current) {
        setExternalCostsComparison(() => ({
          isPending: false,
          data: data.error ? null : data,
        }));
      }
    } catch (error) {
      if (!didUnmount.current) {
        setExternalCostsComparison(() => ({ isPending: false, data: null }));
      }
    }
  };

  const fetchPieChartData = async () => {
    try {
      const regions = await getPieByRegion(entityId, { year, latestUpdate });
      const industriesData = await getPieByIndustry(entityId, { year, latestUpdate });
      const data = {
        regions: regions.map((el) => ({ name: el.region, exposure: el.exposure })),
        industries: industriesData.map((el) => ({ name: el.industry, exposure: el.exposure })),
      };
      if (!didUnmount.current) {
        setStats({ isPending: false, ...data });
      }
    } catch (error) {
      if (!didUnmount.current) {
        setStats({ isPending: false, regions: null, industries: null });
        setPages(6);
      }
    }
  };

  const fetchImpactData = async () => {
    try {
      const countries = await getImpactByCountry(entityId, { year, latestUpdate });
      const industriesData = await getImpactByIndustry(entityId, { year, latestUpdate });
      if (!didUnmount.current) {
        setIndustryData({
          isPending: false,
          data: industriesData.map((el) => ({ ...el, name: el.industry })) || null,
        });
        setCountryData({
          isPending: false,
          data: countries.map((el) => ({ ...el, name: el.country })) || null,
        });
      }
    } catch (error) {
      if (!didUnmount.current) {
        setIndustryData({ isPending: false, data: null });
        setCountryData({ isPending: false, data: null });
        setPages(6);
      }
    }
  };

  useEffect(() => {
    if (getEsgAvailable()) {
      getEntityData();
      getDistribution();
      getImpactOverview();
      getImpactScore();
      getScoreOverview();
      getScoreComparison();
      getExternalCostsComparison();
      getRegionImpact();
      getIndustryImpact();
      getRanking();
      getEntityDataESGScore();
      if (esgLendingData) {
        fetchPieChartData();
        fetchImpactData();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  useEffect(() => {
    return () => {
      didUnmount.current = true;
    };
  }, []);

  useEffect(() => {
    const _showBankLendingSection =
      stats?.regions?.length &&
      stats?.industries?.length &&
      industryData?.data?.length &&
      countryData?.data?.length;
    setShowBankLendingSection(_showBankLendingSection);
    setPages(6);
  }, [stats, industryData, countryData]);

  const ImpactTableData = regionImpact.data && regionImpactToTableParser(regionImpact.data);
  const ImpactMapData = regionImpact.data && regionImpactToMapParser(regionImpact.data);

  const esgUnavailable = !esgAvailable && !impactScore.isPending;

  return (
    <GenerateReportsWrapper>
      <GenerateReportsPage>
        {entityData && entityData.data && (
          <GenerateReportsHeaderBig
            companyName={entityData.data.name}
            industry={entityData.data.classification.name}
            country={entityData.data.countryCode}
          />
        )}
        <Box mb={1}>
          <ESGImpactScore
            score={impactScore.data}
            data={entityData.data}
            loading={!esgUnavailable && impactScore.isPending}
          />
        </Box>
        {impactOverview && impactOverview.data && (
          <Box mb={1}>
            <ESGImpactOverview data={impactOverview.data} loading={impactOverview.isPending} />
          </Box>
        )}
        {distribution && distribution.data && (
          <Box mb={1}>
            <ESGDistribution data={distribution.data} loading={distribution.isPending} />
          </Box>
        )}
        {ranking && ranking.data && (
          <Box>
            <ESGRanking
              data={ranking.data}
              loading={ranking.isPending}
              id={entityId}
              entityName={entityData.data.name}
            />
          </Box>
        )}
        <GenerateReportsFooter pageNum={1} pageTotal={pages} todayDate={actualDate} />
      </GenerateReportsPage>
      <GenerateReportsPage>
        {entityData && entityData.data && (
          <GenerateReportsHeaderSection companyName={entityData.data.name} />
        )}
        {scoreOverview && scoreOverview.data && (
          <Box mb={3}>
            <ESGScoreOverview scores={scoreOverview.data} loading={scoreOverview.isPending} />
          </Box>
        )}
        {esgAvailable && ImpactMapData && (
          <Box>
            <Paper>
              <Box p={2}>
                <Typography variant="h6">{t('ESG impacts by region')}</Typography>
              </Box>
              <Box pb={2}>
                <Divider />
              </Box>
              <ESGImpactMap data={ImpactMapData} loading={regionImpact.isPending} withLegend />
            </Paper>
          </Box>
        )}
        <GenerateReportsFooter pageNum={2} pageTotal={pages} todayDate={actualDate} />
      </GenerateReportsPage>

      <GenerateReportsPage>
        {entityData && entityData.data && (
          <GenerateReportsHeaderSection companyName={entityData.data.name} />
        )}
        {esgAvailable && ImpactTableData && (
          <Box mb={3}>
            <Paper>
              <Box p={2}>
                <Typography variant="h6">{t('ESG impact by region')}</Typography>
              </Box>
              <ESGImpactTable
                data={ImpactTableData}
                loading={regionImpact.isPending}
                type="region"
              />
            </Paper>
          </Box>
        )}
        {esgAvailable && industryImpact && industryImpact.data && (
          <Box>
            <Paper>
              <Box p={2}>
                <Typography variant="h6">{t('ESG impact by industry')}</Typography>
              </Box>
              <ESGImpactTable
                data={industryImpact.data}
                loading={industryImpact.isPending}
                type="industry"
              />
            </Paper>
          </Box>
        )}
        <GenerateReportsFooter pageNum={3} pageTotal={pages} todayDate={actualDate} />
      </GenerateReportsPage>

      {esgLendingData && showBankLendingSection && (
        <GenerateReportsPage>
          {entityData && entityData.data && (
            <GenerateReportsHeaderSection companyName={entityData.data.name} />
          )}
          {stats && stats.regions && stats.industries && (
            <Box mb={3}>
              <ESGLendingData statistics={stats} loading={stats.isPending} />
            </Box>
          )}
          <GenerateReportsFooter pageNum={3} pageTotal={pages} todayDate={actualDate} />
        </GenerateReportsPage>
      )}
      {esgLendingData && showBankLendingSection && (
        <GenerateReportsPage>
          {entityData && entityData.data && (
            <GenerateReportsHeaderSection companyName={entityData.data.name} />
          )}
          {industryData && industryData.data && countryData && countryData.data && (
            <Box mb={3}>
              <ESGLendingImpact
                industries={industryData.data}
                countries={countryData.data}
                loading={industryData.isPending || countryData.isPending}
              />
            </Box>
          )}
          <GenerateReportsFooter pageNum={4} pageTotal={pages} todayDate={actualDate} />
        </GenerateReportsPage>
      )}

      <GenerateReportsPage>
        {entityData && entityData.data && (
          <GenerateReportsHeaderSection companyName={entityData.data.name} />
        )}
        {esgAvailable && scoreComparison && scoreComparison.data && (
          <Box mb={5}>
            <ESGScoreComparison
              scores={scoreComparison.data}
              loading={scoreComparison.isPending}
              id={entityId}
            />
          </Box>
        )}
        <GenerateReportsFooter pageNum={4} pageTotal={pages} todayDate={actualDate} />
      </GenerateReportsPage>

      <GenerateReportsPage>
        {entityData && entityData.data && (
          <GenerateReportsHeaderSection companyName={entityData.data.name} />
        )}
        {esgAvailable &&
          entityData &&
          entityData.data &&
          externalCostsComparison &&
          externalCostsComparison.data && (
            <>
              <Box mb={3}>
                <ESGImpactDistribution
                  data={externalCostsComparison.data}
                  loading={externalCostsComparison.isPending}
                />
              </Box>
            </>
          )}
        <GenerateReportsFooter pageNum={5} pageTotal={pages} todayDate={actualDate} />
      </GenerateReportsPage>

      <GenerateReportsPage>
        {entityData && entityData.data && (
          <GenerateReportsHeaderSection companyName={entityData.data.name} />
        )}
        <Box>
          <Paper>
            <Box p={2}>
              <Typography variant="h6">{t('Disclaimer')}</Typography>
              <Box py={2}>
                <Divider />
              </Box>
              <Grid container>
                <Typography variant="body2">{t(disclaimerDocument)}</Typography>
              </Grid>
            </Box>
          </Paper>
        </Box>
        <GenerateReportsFooter pageNum={6} pageTotal={pages} todayDate={actualDate} />
      </GenerateReportsPage>
    </GenerateReportsWrapper>
  );
};
